import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  Moon, 
  Eye, 
  Heart,
  Package,
  CheckCircle,
  ArrowRight,
  Star,
  CreditCard,
  User,
  Mail,
  Phone,
  Home,
  MessageSquare,
  Send,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Payment card logos - local SVGs in /public/images
const cardLogos = [
  { name: 'Visa', src: '/images/visa.svg', alt: 'Visa' },
  { name: 'Mastercard', src: '/images/mastercard.svg', alt: 'Mastercard' },
  { name: 'Amex', src: '/images/amex.svg', alt: 'American Express' },
  { name: 'Discover', src: '/images/discover.svg', alt: 'Discover' },
];

const EducationPage = () => {
  usePageTitle('Education & Membership');
  const [activeTab, setActiveTab] = useState('membership');
  const [membershipForm, setMembershipForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    tarot_type: '',
    topic: '',
    special_requests: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationId, setRegistrationId] = useState('');

  const handleMembershipChange = (e) => {
    const { name, value } = e.target;
    setMembershipForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleMembershipSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/membership/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(membershipForm),
      });

      if (!response.ok) {
        throw new Error('Registration failed');
      }

      const data = await response.json();
      setRegistrationId(data.id.slice(0, 8).toUpperCase());
      setIsRegistered(true);
      toast.success('Membership registration successful! Check your email for confirmation.');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      if (!response.ok) {
        throw new Error('Message failed to send');
      }

      toast.success('Message sent successfully! We will get back to you soon.');
      setContactForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-[#faf8f5] py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#00C853] to-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-4xl text-[#1e1919] mb-4">Welcome to the Membership!</h1>
          <p className="text-lg text-[#61525a] mb-8">
            Your monthly membership has been registered successfully. 
            A confirmation email has been sent with all the details.
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <p className="text-sm text-[#7A7A9A] uppercase tracking-wider mb-2">Your Membership ID</p>
            <p className="text-3xl font-mono font-bold text-[#9D4EDD]" data-testid="membership-id">{registrationId}</p>
            <div className="mt-6 pt-6 border-t border-[#e8e4de]">
              <p className="text-sm text-[#61525a]">
                <strong>Monthly Benefits:</strong><br/>
                1 Tarot Reading ({membershipForm.tarot_type}) | 2 Psychic Readings | 1 Gemstone Prescription
              </p>
              <p className="text-sm text-[#9D4EDD] mt-2">
                <strong>Focus Topic:</strong> {membershipForm.topic.charAt(0).toUpperCase() + membershipForm.topic.slice(1)}
              </p>
            </div>
          </div>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
          >
            Explore Our Crystal Collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <div className="relative py-20 px-6 bg-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, #9D4EDD 0%, transparent 40%),
                             radial-gradient(circle at 80% 70%, #FFB800 0%, transparent 40%),
                             radial-gradient(circle at 50% 50%, #FF6B9D 0%, transparent 30%)`
          }} />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#c9a96e]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium">
              Gemstone Minerals Education
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Spiritual Wellness Membership
          </h1>
          <p className="text-lg text-[#bbb5ae] max-w-2xl mx-auto">
            Unlock your spiritual potential with our monthly membership program. 
            Receive personalized readings, psychic guidance, and healing gemstone prescriptions.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-[72px] z-40 bg-white shadow-md">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex">
            <button
              onClick={() => setActiveTab('membership')}
              className={`flex-1 py-4 text-center font-semibold transition-all border-b-2 ${
                activeTab === 'membership'
                  ? 'border-[#9D4EDD] text-[#9D4EDD]'
                  : 'border-transparent text-[#7A7A9A] hover:text-[#9D4EDD]'
              }`}
              data-testid="membership-tab"
            >
              Monthly Membership
            </button>
            <button
              onClick={() => setActiveTab('webinar')}
              className={`flex-1 py-4 text-center font-semibold transition-all border-b-2 ${
                activeTab === 'webinar'
                  ? 'border-[#9D4EDD] text-[#9D4EDD]'
                  : 'border-transparent text-[#7A7A9A] hover:text-[#9D4EDD]'
              }`}
              data-testid="webinar-tab"
            >
              Live Webinars
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex-1 py-4 text-center font-semibold transition-all border-b-2 ${
                activeTab === 'contact'
                  ? 'border-[#9D4EDD] text-[#9D4EDD]'
                  : 'border-transparent text-[#7A7A9A] hover:text-[#9D4EDD]'
              }`}
              data-testid="contact-tab"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* Membership Tab */}
      {activeTab === 'membership' && (
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Package Details Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-40 space-y-6">
                {/* Price Card */}
                <div className="bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] rounded-2xl p-8 text-white shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 fill-current" />
                    <span className="text-sm uppercase tracking-wider opacity-80">Monthly Package</span>
                  </div>
                  <p className="text-5xl font-serif mb-2">$300</p>
                  <p className="text-sm opacity-80">per month</p>
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <p className="text-sm font-semibold mb-4">Package Includes:</p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Moon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">1 Tarot Reading</p>
                          <p className="text-xs opacity-80">Egyptian, Moon, or Regular</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Eye className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">2 Psychic Readings</p>
                          <p className="text-xs opacity-80">Personalized guidance</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Package className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">1 Healing Prescription</p>
                          <p className="text-xs opacity-80">Gemstone sent by post</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#9D4EDD]" />
                    Secure Payment
                  </h3>
                  <p className="text-sm text-[#61525a] mb-4">
                    We accept all major credit and debit cards
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {cardLogos.map((card) => (
                      <div key={card.name} className="bg-[#faf8f5] p-2 rounded-lg">
                        <img src={card.src} alt={card.alt} className="h-8 w-auto" data-testid={`card-logo-${card.name.toLowerCase()}`} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#e8e4de]">
                    <div className="flex items-center gap-2 text-sm text-[#00C853]">
                      <CheckCircle className="w-4 h-4" />
                      <span>256-bit SSL Encrypted</span>
                    </div>
                  </div>
                </div>

                {/* Topics Info */}
                <div className="bg-[#F8F5FF] rounded-2xl p-6">
                  <h3 className="font-serif text-lg text-[#1e1919] mb-4">Focus Topics</h3>
                  <p className="text-sm text-[#61525a] mb-4">
                    Select a topic that resonates with your current life journey:
                  </p>
                  <ul className="space-y-2">
                    {[
                      { name: 'Business', desc: 'Career growth & success' },
                      { name: 'Wealth', desc: 'Financial abundance' },
                      { name: 'Healing', desc: 'Physical & emotional wellness' },
                      { name: 'Romance', desc: 'Love & relationships' }
                    ].map((topic, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Heart className="w-3 h-3 text-[#FF6B9D]" />
                        <span className="text-[#1e1919] font-medium">{topic.name}:</span>
                        <span className="text-[#61525a]">{topic.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Membership Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="font-serif text-2xl text-[#1e1919] mb-2">Join Our Monthly Membership</h2>
                <p className="text-[#61525a] mb-8">Complete the form below to start your spiritual wellness journey.</p>

                <form onSubmit={handleMembershipSubmit} className="space-y-6" data-testid="membership-form">
                  {/* Personal Information */}
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#9D4EDD]" />
                      Personal Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">First Name *</label>
                        <input
                          type="text"
                          name="first_name"
                          value={membershipForm.first_name}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="Enter your first name"
                          data-testid="membership-first-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Last Name *</label>
                        <input
                          type="text"
                          name="last_name"
                          value={membershipForm.last_name}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="Enter your last name"
                          data-testid="membership-last-name"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#9D4EDD]" />
                      Contact Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={membershipForm.email}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="your@email.com"
                          data-testid="membership-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={membershipForm.phone}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="+1 (555) 000-0000"
                          data-testid="membership-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                      <Home className="w-5 h-5 text-[#9D4EDD]" />
                      Shipping Address
                      <span className="text-xs font-normal text-[#7A7A9A]">(for gemstone delivery)</span>
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Street Address *</label>
                        <input
                          type="text"
                          name="address"
                          value={membershipForm.address}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          placeholder="123 Main Street"
                          data-testid="membership-address"
                        />
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#4A4A6A] mb-2">City *</label>
                          <input
                            type="text"
                            name="city"
                            value={membershipForm.city}
                            onChange={handleMembershipChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                            placeholder="City"
                            data-testid="membership-city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#4A4A6A] mb-2">State *</label>
                          <input
                            type="text"
                            name="state"
                            value={membershipForm.state}
                            onChange={handleMembershipChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                            placeholder="State"
                            data-testid="membership-state"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#4A4A6A] mb-2">ZIP Code *</label>
                          <input
                            type="text"
                            name="zip_code"
                            value={membershipForm.zip_code}
                            onChange={handleMembershipChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                            placeholder="12345"
                            data-testid="membership-zip"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Country</label>
                        <input
                          type="text"
                          name="country"
                          value={membershipForm.country}
                          onChange={handleMembershipChange}
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] bg-[#faf8f5] outline-none"
                          data-testid="membership-country"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Service Preferences */}
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-4 flex items-center gap-2">
                      <Moon className="w-5 h-5 text-[#9D4EDD]" />
                      Service Preferences
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Tarot Reading Type *</label>
                        <select
                          name="tarot_type"
                          value={membershipForm.tarot_type}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          data-testid="tarot-select"
                        >
                          <option value="">Select tarot type</option>
                          <option value="egyptian">Egyptian Tarot</option>
                          <option value="moon">Moon Tarot</option>
                          <option value="regular">Regular Tarot</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Focus Topic *</label>
                        <select
                          name="topic"
                          value={membershipForm.topic}
                          onChange={handleMembershipChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                          data-testid="topic-select"
                        >
                          <option value="">Select your focus topic</option>
                          <option value="business">Business - Career & Success</option>
                          <option value="wealth">Wealth - Financial Abundance</option>
                          <option value="healing">Healing - Wellness & Recovery</option>
                          <option value="romance">Romance - Love & Relationships</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Special Requests or Notes</label>
                      <textarea
                        name="special_requests"
                        value={membershipForm.special_requests}
                        onChange={handleMembershipChange}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none resize-none"
                        placeholder="Any specific areas you'd like us to focus on..."
                        data-testid="special-requests"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white py-4 rounded-xl font-semibold text-lg hover:shadow-[0_8px_25px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                      data-testid="membership-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Start Membership - $300/month
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-center text-[#7A7A9A] mt-4">
                      By subscribing, you agree to our terms and conditions. 
                      Cancel anytime with 30 days notice.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Webinar Tab */}
      {activeTab === 'webinar' && (
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl text-[#1e1919] mb-4">Upcoming Live Webinars</h2>
              <p className="text-[#61525a]">Learn from expert educators in our exclusive live events</p>
            </div>

            {/* Featured Webinar */}
            <div className="bg-gradient-to-br from-[#F8F5FF] to-[#FFF5F8] rounded-2xl p-8 mb-8">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="w-32 h-32 rounded-2xl overflow-hidden flex-shrink-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_12c5979f-07d4-4fbe-88fc-c2d666777007/artifacts/3xo7hul1_Facetune_16-11-2024-19-08-45.jpeg"
                    alt="Romona Nicole Schmidt"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <Calendar className="w-4 h-4 text-[#c9a96e]" />
                    <span className="text-sm text-[#c9a96e] font-medium">July 2026</span>
                  </div>
                  <h3 className="font-serif text-2xl text-[#1e1919] mb-2">Selenite Healing Mastery</h3>
                  <p className="text-[#61525a] mb-4">
                    Join guest educator <strong>Romona Nicole Schmidt</strong> for an in-depth exploration 
                    of Selenite's healing properties and practical applications.
                  </p>
                  <div className="flex items-center gap-4 justify-center md:justify-start">
                    <span className="text-2xl font-bold text-[#9D4EDD]">$800</span>
                    <Link
                      to="/webinar-registration"
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                      data-testid="register-webinar-link"
                    >
                      Register Now
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/kholey"
                className="text-[#9D4EDD] font-semibold hover:underline inline-flex items-center gap-1"
              >
                Learn more about Romona Nicole Schmidt
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl text-[#1e1919] mb-6">Get in Touch</h2>
              <p className="text-[#61525a] mb-8 leading-relaxed">
                Have questions about our membership program or services? 
                We're here to help guide you on your spiritual wellness journey.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#9D4EDD]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#9D4EDD]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-1">Phone</h3>
                    <a href="tel:+14244507018" className="text-[#9D4EDD] hover:underline">
                      +1 (424) 450-7018
                    </a>
                    <p className="text-sm text-[#7A7A9A]">Mon-Fri 9AM-6PM PST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF6B9D]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#FF6B9D]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-1">Email</h3>
                    <a href="mailto:info@gemstoneminerals.com" className="text-[#FF6B9D] hover:underline">
                      info@gemstoneminerals.com
                    </a>
                    <p className="text-sm text-[#7A7A9A]">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00C853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Home className="w-5 h-5 text-[#00C853]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e1919] mb-1">Location</h3>
                    <p className="text-[#61525a]">Los Angeles, California</p>
                    <p className="text-sm text-[#7A7A9A]">United States</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="font-serif text-xl text-[#1e1919] mb-6">Send us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                    placeholder="John Doe"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                    placeholder="your@email.com"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                    placeholder="+1 (555) 000-0000"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Subject *</label>
                  <select
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none"
                    data-testid="contact-subject"
                  >
                    <option value="">Select a subject</option>
                    <option value="membership">Membership Inquiry</option>
                    <option value="webinar">Webinar Question</option>
                    <option value="products">Product Question</option>
                    <option value="support">General Support</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4A6A] mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:border-[#9D4EDD] focus:ring-2 focus:ring-[#9D4EDD]/20 transition-all outline-none resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white py-4 rounded-xl font-semibold hover:shadow-[0_8px_25px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationPage;
